import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import add from 'date-fns/add';
import Logger from 'utilities/logger';

import Typography from 'components/CncUI/Typography';
import Loader from 'components/Loader';
import NotificationRecipient from './NotificationRecipient';
import NotificationBuilder from './NotificationBuilder';
import NotificationPreview from './NotificationPreview';
import formatNotification from './formatNotification';

import withPermissionProtection from 'hoc/withPermissionProtection';
import useData from 'hooks/useData';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import useSnackbar from 'hooks/useSnackbar';

const useStyles = makeStyles(({ spacing }) => ({
  title: {
    marginTop: spacing(5),
  },
}));

const NotificationSender = () => {
  const logger = Logger('NotificationSender');
  const classes = useStyles();
  const notify = useSnackbar();
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      startAt: 'now',
      expireAt: '7d',
      startAtDate: new Date(),
      expireAtDate: add(new Date(), { days: 7 }),
      // Icon Type
      type: '',
      referenceType: 'loginId',
      groupSelect: '',
      origin: '',
    },
  });

  const formData = methods.watch();
  const [groups, setGroups] = useState([]);
  const [state, modify] = useData({
    loading: false,
    loadingGroups: true,
    user: {},
    submitDisabled: true,
    searchError: '',
  });

  const getGroups = useQuery('getOktaGroups', {
    scopes: ['notification:create', 'openid'],
    response: res => res.data.getOktaGroups,
  });

  useEffect(() => {
    getGroups()
      .then((result = []) => {
        logger.info(result);
        setGroups(result);
      })
      .catch(err => logger.error(err))
      .finally(() => modify({ loadingGroups: false }));
  }, []);

  const sendUserNotification = useMutation('sendUserNotification', {
    scopes: ['notification:create', 'openid'],
    request: data => ({
      input: {
        ...data,
        settings: JSON.stringify(data.settings),
        startAt: new Date(data.startAt),
        expireAt: new Date(data.expireAt),
      },
    }),
    response: res => res.data.sendUserNotification || {},
  });

  const onSearch = () => modify({ loading: true });
  const onFound = data => modify({ loading: false, user: data });
  const onClear = () => modify({ loading: false, user: {} });
  const onSubmit = async data => {
    const notification = {
      ...formatNotification(data),
      referenceId:
        formData.referenceType === 'loginId'
          ? `uid_${state.user.loginId}`
          : `gid_${formData.groupSelect?.id}`,
    };
    try {
      await sendUserNotification(notification);
      notify({
        type: 'success',
        message: 'Notification has been sent',
      });
      onClear();
      methods.reset();
    } catch (error) {
      notify({
        type: 'error',
        message: 'Something went wrong. Review the information and try again.',
      });
      console.error(error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={2} id="NotificationBuilder">
          <Grid item xs={12}>
            <Typography variant="h2" id="HeaderTitle" className={classes.title}>
              Notification Creator
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            container
            spacing={2}
            className={classes.form}
          >
            <Grid item xs={12}>
              {state.loadingGroups && <Loader message="Loading Okta Groups" />}
              {!state.loadingGroups && (
                <NotificationRecipient
                  loading={state.loading}
                  onSearch={onSearch}
                  onFound={onFound}
                  onClear={onClear}
                  user={state.user}
                  groups={groups}
                  state={state}
                  modify={modify}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <NotificationBuilder state={state} modify={modify} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} className={classes.form}>
            <NotificationPreview
              user={state.user}
              groups={groups}
              notificationForm={formData}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default withPermissionProtection('notification:create')(
  NotificationSender,
);
